import { cn } from '@workspace/utils';
import { useMemo } from 'react';
import { useGlobalForm } from '../contexts/global-form-context';

interface StepperProps {
  className?: string;
  stepsToShow?: number;
}

export function Stepper({ className, stepsToShow = 4 }: StepperProps) {
  const { meta, actions, steps, disableNavigation } = useGlobalForm();
  const { step: activeStep, subStep: indexQuestion, totalSubSteps } = meta;

  const { displayedSteps, pourcentage } = useMemo(() => {
    let startStep = Math.max(0, activeStep - (stepsToShow - 2));
    const maxStartIndex = Math.max(0, steps.length - stepsToShow);
    startStep = Math.min(startStep, maxStartIndex);

    const pourcentage = Math.round((indexQuestion / (totalSubSteps + 1)) * 100);

    return {
      displayedSteps: steps.slice(startStep, startStep + stepsToShow),
      pourcentage,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, indexQuestion, totalSubSteps, stepsToShow]);

  const handleUpdateStep = (index: number) => {
    if (!disableNavigation) {
      actions.updateStep(index);
    }
  };

  return (
    <nav className={cn('flex gap-4 text-center w-full', className)}>
      {displayedSteps.map((s) => {
        const isCompleted = s.index < activeStep;
        const isActive = s.index === activeStep;
        const progressWidth = isCompleted ? 100 : isActive ? pourcentage : 0;

        return (
          <div
            className={cn(
              'flex-1 text-xs relative font-medium transition-all duration-300',
              isActive ? 'block' : 'hidden md:block'
            )}
            key={s.index}
          >
            <span
              className={cn(
                'transition-colors duration-200 cursor-pointer',
                isCompleted && !disableNavigation && 'hover:underline',
                isCompleted && disableNavigation && 'cursor-not-allowed',
                isCompleted ? 'text-stepper-completed' :
                  isActive ? 'text-stepper-active' : 'text-stepper-inactive'
              )}
              onClick={() => isCompleted && handleUpdateStep(s.index)}
              role={isCompleted && !disableNavigation ? 'button' : undefined}
              tabIndex={isCompleted && !disableNavigation ? 0 : undefined}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && isCompleted && !disableNavigation) {
                  e.preventDefault();
                  handleUpdateStep(s.index);
                }
              }}
            >
              {s.title}
            </span>

            <div className="overflow-hidden h-1 text-xs flex rounded bg-stepper-line mt-1.5 transition-all duration-300">
              <div
                className={cn(
                  'shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-300',
                  isCompleted ? 'bg-stepper-completed' : isActive ? 'bg-stepper-active' : 'bg-transparent'
                )}
                style={{ width: `${progressWidth}%` }}
                role="progressbar"
                aria-valuenow={progressWidth}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Étape ${s.index + 1}: ${s.title}`}
              />
            </div>
          </div>
        );
      })}
    </nav>
  );
}
